import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, ExternalLink } from 'lucide-react';
import { MembershipRequestService, CreateRequestData, LicenseData } from '../../services/MembershipRequestService';
import { AircraftService } from '../../services/aircraftService';
import { Aircraft } from '../../types/aircraft';
import { Organization } from '../../types/organization';
import { AircraftPreviewCard } from './AircraftPreviewCard';
import { logger } from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';

interface JoinClubRequestFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (requestId: string) => void;
  organization: Organization;
  joinCode: string;
}

export const JoinClubRequestForm: React.FC<JoinClubRequestFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  organization,
  joinCode,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [aircraft, setAircraft] = useState<Aircraft[]>([]);
  const [loadingAircraft, setLoadingAircraft] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    licenseType: '' as LicenseData['type'] | '',
    licenseNumber: '',
    licenseCountry: '',
    licenseExpiry: '',
    totalHours: '',
    ratings: [] as string[],
    licenseNotes: '',
  });

  const loadAircraft = useCallback(async () => {
    try {
      setLoadingAircraft(true);
      const aircraftList = await AircraftService.getAircraftPreview(organization.id);
      setAircraft(aircraftList);
    } catch (err) {
      logger.error('Error loading aircraft:', err);
    } finally {
      setLoadingAircraft(false);
    }
  }, [organization.id]);

  // Load aircraft when modal opens
  useEffect(() => {
    if (isOpen && organization.id) {
      loadAircraft();
    }
  }, [isOpen, organization.id, loadAircraft]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleRatingToggle = (rating: string) => {
    setFormData(prev => ({
      ...prev,
      ratings: prev.ratings.includes(rating)
        ? prev.ratings.filter(r => r !== rating)
        : [...prev.ratings, rating],
    }));
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!formData.email.trim()) {
      errors.push('Email is required');
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.push('Please enter a valid email address');
      }
    }

    if (!formData.firstName.trim()) {
      errors.push('First name is required');
    }

    if (!formData.lastName.trim()) {
      errors.push('Last name is required');
    }

    if (!formData.phoneNumber.trim()) {
      errors.push('Phone number is required');
    }

    // Validate phone number format (basic check)
    if (formData.phoneNumber.trim() && formData.phoneNumber.trim().length < 10) {
      errors.push('Please enter a valid phone number');
    }

    // Validate license hours if provided
    if (formData.totalHours && (isNaN(Number(formData.totalHours)) || Number(formData.totalHours) < 0)) {
      errors.push('Total hours must be a valid positive number');
    }

    if (errors.length > 0) {
      setError(errors[0]); // Show first error
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    // Check if user already has a pending request
    try {
      const hasPending = await MembershipRequestService.hasPendingRequest(
        formData.email,
        organization.id
      );

      if (hasPending) {
        setError('You already have a pending membership request for this club. Please wait for it to be reviewed or check your email for the status link.');
        return;
      }
    } catch (err) {
      logger.error('Error checking pending request:', err);
      // Continue anyway - might be a network issue, but user will get error if duplicate
    }

    setIsSubmitting(true);

    try {
      // Build license data if any license info provided
      let licenseData: LicenseData | undefined;
      if (formData.licenseType || formData.licenseNumber || formData.totalHours) {
        licenseData = {
          type: formData.licenseType || undefined,
          number: formData.licenseNumber || undefined,
          country: formData.licenseCountry || undefined,
          expiryDate: formData.licenseExpiry || undefined,
          ratings: formData.ratings.length > 0 ? formData.ratings : undefined,
          totalHours: formData.totalHours ? parseInt(formData.totalHours, 10) : undefined,
          notes: formData.licenseNotes || undefined,
        };
      }

      const requestData: CreateRequestData = {
        orgId: organization.id,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        licenseData,
        userId: currentUser?.uid,
      };

      const request = await MembershipRequestService.createRequest(requestData);

      // Store request ID and show success
      setCreatedRequestId(request.id);
      setRequestSuccess(true);

      // Call success callback
      onSuccess?.(request.id);
    } catch (err: any) {
      logger.error('Error creating membership request:', err);
      
      // Handle specific error cases
      if (err.message?.includes('duplicate') || err.message?.includes('already exists')) {
        setError('You already have a pending membership request for this club. Please check your email for the status.');
      } else if (err.message?.includes('organization')) {
        setError('Invalid organization. Please check the join code and try again.');
      } else {
        setError(err.message || 'Failed to submit membership request. Please try again.');
      }
      
      setIsSubmitting(false);
    }
  };

  const handleViewStatus = () => {
    if (createdRequestId) {
      navigate(`/membership-request/${createdRequestId}`);
    }
    onClose();
  };

  // Show success screen after request is created
  if (requestSuccess && createdRequestId && isOpen) {
    return isOpen ? (
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-lg shadow-xl p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">
                Request Submitted Successfully
              </Dialog.Title>
              <p className="text-sm text-gray-600 mb-6">
                Your membership request has been submitted to <strong>{organization.name}</strong>.
                You'll receive an email notification when it's reviewed.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleViewStatus}
                  className="w-full flex items-center justify-center"
                >
                  View Request Status
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    ) : null;
  }

  const commonRatings = ['IFR', 'MEL', 'SES', 'MES', 'TW', 'HP', 'Complex'];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-3xl w-full bg-white rounded-lg shadow-xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-gray-900">
              Join {organization.name}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Organization Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">{organization.name}</h3>
              {organization.description && (
                <p className="text-sm text-gray-600 mb-2">{organization.description}</p>
              )}
              {organization.baseAirport && (
                <p className="text-sm text-gray-600">
                  Base Airport: {organization.baseAirport}
                </p>
              )}
            </div>

            {/* Aircraft Preview */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Available Aircraft</h3>
              {loadingAircraft ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-500">Loading aircraft...</span>
                </div>
              ) : aircraft.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto p-1">
                  {aircraft.map((ac) => (
                    <AircraftPreviewCard key={ac.id} aircraft={ac} />
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg text-center">
                  No aircraft information available
                </div>
              )}
            </div>

            {/* Required Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Your Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This is what the club will use to contact you — it doesn't need to match the email you signed in with.
                  </p>
                </div>

                <div>
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <Input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Optional License Information */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-medium text-gray-900">Pilot License Information (Optional)</h3>
              <p className="text-sm text-gray-500">
                You can join without a license. If you have a pilot license, you can provide details below.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="licenseType">License Type</Label>
                  <select
                    id="licenseType"
                    name="licenseType"
                    value={formData.licenseType}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm appearance-none cursor-pointer focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select license type</option>
                    <option value="PPL">PPL - Private Pilot License</option>
                    <option value="CPL">CPL - Commercial Pilot License</option>
                    <option value="ATP">ATP - Airline Transport Pilot</option>
                    <option value="SPL">SPL - Sport Pilot License</option>
                    <option value="ULM">ULM - Ultralight</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="licenseNumber">License Number</Label>
                  <Input
                    id="licenseNumber"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    placeholder="License number"
                  />
                </div>

                <div>
                  <Label htmlFor="licenseCountry">Issuing Country</Label>
                  <Input
                    id="licenseCountry"
                    name="licenseCountry"
                    value={formData.licenseCountry}
                    onChange={handleInputChange}
                    placeholder="e.g., USA, Canada"
                  />
                </div>

                <div>
                  <Label htmlFor="licenseExpiry">Expiry Date</Label>
                  <Input
                    id="licenseExpiry"
                    name="licenseExpiry"
                    type="date"
                    value={formData.licenseExpiry}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="totalHours">Total Flight Hours</Label>
                  <Input
                    id="totalHours"
                    name="totalHours"
                    type="number"
                    min="0"
                    value={formData.totalHours}
                    onChange={handleInputChange}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label>Ratings (select all that apply)</Label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {commonRatings.map((rating) => (
                    <label
                      key={rating}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.ratings.includes(rating)}
                        onChange={() => handleRatingToggle(rating)}
                        className="h-4 w-4 rounded border border-gray-300 text-blue-600 accent-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{rating}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="licenseNotes">Additional Notes</Label>
                <textarea
                  id="licenseNotes"
                  name="licenseNotes"
                  value={formData.licenseNotes}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Any additional information about your qualifications..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

